import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { extendObservable } from 'mobx'
import PropTypes from 'prop-types'
import { Field } from 'formik'
import find from 'lodash/find'
import get from 'lodash/get'
import BigNumber from 'bignumber.js'

import DatePicker from '@components/Form/DatePicker'
import { Dropdown } from '@components/Form/Autocomplete'
import Input from '@components/Form/Input'
import TextArea from '@components/Form/TextArea'
import Checkbox from '@components/Form/Checkbox'
import InfoIcon from '@components/Icon/Info'
import InfoBox from '@components/InfoBox'

class FeeItem extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    sidebarStore: PropTypes.object,
    feeFormStore: PropTypes.object,
    form: PropTypes.object,
    disabled: PropTypes.bool,
    minDeliveryDate: PropTypes.instanceOf(Date),
    maxDeliveryDate: PropTypes.instanceOf(Date),
    freeCall: PropTypes.bool,
    isCapiLopProjectType: PropTypes.bool
  }

  constructor(props) {
    super(props)
    extendObservable(this, {
      contractCreditValue: null
    })

    this.setDefaultActiveContract()
  }

  componentDidUpdate() {
    const { form } = this.props
    const contract = find(this.getContractOptions(), {
      id: form.values.contractId
    })

    this.contractCreditValue = get(contract, 'creditValue', null)
  }

  shouldShowCredits() {
    const { form, staticData } = this.props
    const { CONTRACT_TYPES } = staticData.CONST
    const contractTypeId = form.values.contractTypeId
    return contractTypeId && contractTypeId !== CONTRACT_TYPES.PAYG
  }

  openClientAccountSidebar = e => {
    e.preventDefault()
    this.props.sidebarStore.openSidebar({
      template: 'client-account',
      id: this.props.feeFormStore.project.accountId
    })
  }

  openContractSidebar = () => {
    this.props.sidebarStore.openSidebar({
      template: 'contract',
      id: this.props.form.values.contractId
    })
  }

  getContractOptions() {
    const { applicableContracts, fee } = this.props.feeFormStore
    const contracts = [...applicableContracts]

    const contract = get(fee, 'contract')

    if (contract) {
      const hasContract = find(contracts, { id: contract.id })
      if (!hasContract) {
        contracts.unshift(contract)
      }
    }

    return [{ id: 0, label: 'No contract' }, ...contracts]
  }

  setDefaultActiveContract() {
    const { fee } = this.props.feeFormStore

    if (fee.id) return

    const contract = this.getContractOptions()[1]

    if (contract) {
      const { form, staticData } = this.props
      const { CONTRACT_TYPES } = staticData.CONST

      form.setFieldValue('contractId', contract.id, false)
      form.setFieldValue('contractTypeId', contract.contractTypeId, false)

      if (contract.contractTypeId !== CONTRACT_TYPES.PAYG) {
        if (fee.credits && contract.creditValue) {
          const feeValue = BigNumber(fee.credits)
            .times(contract.creditValue)
            .toNumber()
          form.setFieldValue('feeValue', feeValue, false)
        }
        contract.currencyId
          ? form.setFieldValue('feeCurrencyId', contract.currencyId, false)
          : form.setFieldValue('feeCurrencyId', null, false)
      }
    }
  }

  onContractChange = contract => {
    if (!contract) return

    const {
      form,
      staticData,
      feeFormStore: { fee }
    } = this.props
    const { CONTRACT_TYPES } = staticData.CONST

    form.setFieldValue('contractId', contract.id, false)
    form.setFieldValue('contractTypeId', contract.contractTypeId, false)

    if (contract.contractTypeId === CONTRACT_TYPES.PAYG) {
      form.setFieldValue('feeValue', null, false)
      form.setFieldValue('credits', null)
    } else {
      if (fee.credits && contract.creditValue) {
        const feeValue = BigNumber(fee.credits)
          .times(contract.creditValue)
          .toNumber()
        form.setFieldValue('feeValue', feeValue, false)
      } else {
        form.setFieldValue('feeValue', null, false)
      }
      contract.currencyId
        ? form.setFieldValue('feeCurrencyId', contract.currencyId, false)
        : form.setFieldValue('feeCurrencyId', null, false)
    }
  }

  areCreditsEnabled = contractId => {
    if (!contractId) return false

    const isNoContractSelected = parseInt(contractId, 10) === 0
    if (isNoContractSelected) return false

    const { staticData, form } = this.props
    const { CONTRACT_TYPES } = staticData.CONST
    const creditsEnabled = form.values.contractTypeId !== CONTRACT_TYPES.PAYG

    return creditsEnabled
  }

  renderOptionValue = option => {
    const { contractCoverages } = this.props.staticData.names
    const coverage = contractCoverages[option.contractCoverageId]

    return option.id ? (
      <span>
        {option.contractReference} ({coverage})
      </span>
    ) : (
      option.label
    )
  }

  render() {
    const {
      staticData,
      form,
      disabled,
      minDeliveryDate,
      maxDeliveryDate,
      feeFormStore: {
        fee,
        project: {
          office: { account }
        }
      },
      isCapiLopProjectType,
      isLopWithInvoicingEntity,
    } = this.props

    const { DELIVERABLE_TYPES, FEE_TYPES } = staticData.CONST
    const shouldShowDuration =
      [
        DELIVERABLE_TYPES.ES_CONFERENCE_CALL,
        DELIVERABLE_TYPES.ES_IN_PERSON_INTERVIEW,
        DELIVERABLE_TYPES.ES_IN_PERSON_MEETING,
        DELIVERABLE_TYPES.ES_WEBINAR,
        DELIVERABLE_TYPES.EBR_CONFERENCE_CALL,
        DELIVERABLE_TYPES.EBR_RECRUITMENT_INTERVIEW_EXECUTION
      ].includes(form.values.deliverableType) && fee.type !== 'FEE'

    const lopProjectDescription = isLopWithInvoicingEntity ? 'has a valid charge code' :'e.g. marketing/discount/proposal'

    return (
      <div className="expert-form__section">
        <h2 className="expert-form__subtitle">Fee details</h2>
        {fee.type === 'FEECOST' && (isCapiLopProjectType && !isLopWithInvoicingEntity) ?
          (
            <p>
              <InfoBox theme='blue' icon={<InfoIcon />}>
              This is a <strong>LOP</strong> project type and free call (e.g. marketing/discount/proposal)
              </InfoBox>
            </p>
          )
          : (
            <Field
              name="freeCallId"
              label={`Is this a free call? (${lopProjectDescription})`}
              onChange={this.props.onFreeCallChange}
              component={Checkbox}
              value={this.props.freeCall}
            />
          )}
        {(!isCapiLopProjectType || isLopWithInvoicingEntity) &&
          <Field
            name="feeTypeId"
            placeholder="Select fee type"
            label="Fee type"
            itemToString={item => item && item.name}
            options={staticData.feeTypes.filter(f => f.id !== FEE_TYPES.OTHER)}
            disabled={disabled || this.props.freeCall}
            component={Dropdown}
          />}
        <div className="expert-form__input-group">
          <label className="expert-form__label">
            {shouldShowDuration ? 'Interview date / duration' : 'Date'}
          </label>
          <div
            className="expert-form__input-wrapper expert-form__input-wrapper__inline-children"
            data-cy="delivery-date">
            <Field
              name="deliveryDate"
              dateOnly
              minDate={disabled ? undefined : minDeliveryDate}
              maxDate={disabled ? undefined : maxDeliveryDate}
              onChange={async value => {
                form.setFieldValue('deliveryDate', value, false)
                form.setFieldTouched('deliveryDate', true)

                // NOTE: This is a workaround for
                // https://github.com/jaredpalmer/formik/issues/529
                await Promise.resolve()
              }}
              disabled={disabled}
              component={DatePicker}
            />
            <div>
              {shouldShowDuration ? (
                <Field
                  name="duration"
                  short
                  disabled={disabled}
                  onChange={async e => {
                    form.setFieldValue('duration', e.target.value, false)
                    form.setFieldTouched('duration', true)

                    // NOTE: This is a workaround for
                    // https://github.com/jaredpalmer/formik/issues/529
                    await Promise.resolve()
                  }}
                  component={Input}
                />
              ) : null}
            </div>
            <div className="m-t-md">
              {shouldShowDuration ? 'minutes' : null}
            </div>
          </div>
        </div>
        <div className="expert-form__input-group">
          <label className="expert-form__label">Fee value</label>
          <div className="expert-form__input-wrapper expert-form__input-wrapper__inline-children">
            <Field
              noLabel
              extraShort
              data-cy="currency-dropdown"
              name="feeCurrencyId"
              placeholder="Select currency"
              itemToString={item => item && item.name}
              options={staticData.currencies}
              onChange={async value => {
                form.setFieldValue('feeCurrencyId', value.id, false)
                form.setFieldTouched('feeCurrencyId', true)

                // NOTE: This is a workaround for
                // https://github.com/jaredpalmer/formik/issues/529
                await Promise.resolve()
              }}
              disabled={this.areCreditsEnabled(form.values.contractId)}
              component={Dropdown}
            />
            <Field
              name="feeValue"
              placeholder="Amount"
              extraShort
              onChange={e => {
                form.setFieldValue('feeValue', e.target.value, false)
                form.setFieldTouched('feeValue', true)

                if (this.areCreditsEnabled(form.values.contractId)) {
                  const credits = BigNumber(e.target.value || 0)
                    .dividedBy(this.contractCreditValue)
                    .decimalPlaces(6)

                  form.setFieldValue('credits', credits, false)
                  form.setFieldTouched('credits', true)
                }
              }}
              component={Input}
            />
            <div />
          </div>
        </div>
        <Field
          name="credits"
          placeholder="Credits"
          label="Credits"
          extraShort
          onChange={e => {
            form.setFieldValue('credits', e.target.value, false)
            form.setFieldTouched('credits', true)

            const feeValue = BigNumber(e.target.value || 0)
              .times(this.contractCreditValue)
              .decimalPlaces(2)

            form.setFieldValue('feeValue', feeValue, false)
            form.setFieldTouched('feeValue', true)
          }}
          disabled={!this.areCreditsEnabled(form.values.contractId)}
          component={Input}
        />
        <div className="expert-form__input-group">
          <label className="expert-form__label">Client Account</label>
          <div className="expert-form__input-wrapper">
            <span className="link" onClick={this.openClientAccountSidebar}>
              {account.companyName}
            </span>
          </div>
        </div>
        <Field
          name="contractId"
          data-cy="contract-dropdown"
          placeholder="Select contract"
          label="Contract"
          options={this.getContractOptions()}
          itemToString={this.renderOptionValue}
          onChange={this.onContractChange}
          linkCallback={this.openContractSidebar}
          linkLabel={form.values.contractId ? 'DETAILS' : ''}
          disabled={disabled}
          component={Dropdown}
        />
        {fee.type === 'FEE' || fee.type === 'FEECOST' ? (
          <Field
            name="feeNote"
            label="Note"
            placeholder=""
            disabled={disabled}
            component={TextArea}
          />
        ) : null}
      </div>
    )
  }
}

export default inject('sidebarStore', 'staticData')(observer(FeeItem))
